import { forwardRef } from 'react';

import { Pressable } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import type { QuizButtonProps } from './QuizButton.types';
import {
  quizBadgeTextVariants,
  quizBadgeVariants,
  quizButtonTextVariants,
  quizButtonVariants,
} from './QuizButton.variants';
import { QuizButtonCrossfade } from './QuizButtonCrossfade';

// Detached ref shape — see Pressable.tsx / Button.tsx / Chip.tsx for the
// rationale (pnpm hoists multiple @types/react copies; React's `Ref<T>`
// becomes nominally distinct across consumer apps even when versions match).
type AnyRef<T> = ((node: T | null) => void) | { current: T | null } | null;

/**
 * Cross-platform quiz-option button. Renders identically on web and React
 * Native. A full-width pill with a leading letter badge + option text.
 *
 * The `variant` prop IS the answer-state axis: `default` (unanswered) ·
 * `primary` (selected) · `correct` / `incorrect` (revealed results).
 *
 * @example
 * ```tsx
 * import { QuizButton } from '@flashka/shared/ui';
 *
 * // Unanswered option
 * <QuizButton letter="A" onPress={() => choose('A')}>Mitochondria</QuizButton>
 *
 * // Selected, then revealed states
 * <QuizButton variant="primary" letter="B">Nucleus</QuizButton>
 * <QuizButton variant="correct" letter="A">Mitochondria</QuizButton>
 * <QuizButton variant="incorrect" letter="C">Ribosome</QuizButton>
 * ```
 */
function QuizButtonInner(
  {
    children,
    letter,
    onPress,
    variant,
    fullWidth,
    disabled = false,
    haptic = 'selection',
    className,
    textClassName,
    badgeClassName,
    accessibilityLabel,
    testID,
    webProps,
    nativeProps,
    onHoverIn,
    onHoverOut,
    // Slot-style consumers (Radix `Trigger asChild`, etc.) clone the button and
    // attach extra props / a ref at the top level — funnel them through to the
    // Pressable, which composes them with its own handlers.
    ...injected
  }: QuizButtonProps & Record<string, unknown>,
  ref: AnyRef<HTMLElement>,
) {
  const resolvedVariant = variant ?? 'default';

  const containerClass = cn(
    quizButtonVariants({ variant: resolvedVariant, fullWidth, disabled }),
    className,
  );

  // Wrap a text-like child (string / number / array of those) in Text so the
  // variant typography classes apply. Pass JSX through untouched.
  const label = isTextLike(children) ? (
    <Text
      className={cn(quizButtonTextVariants({ variant: resolvedVariant }), textClassName)}>
      {children}
    </Text>
  ) : (
    children
  );

  const badge =
    letter != null ? (
      <View
        className={cn(quizBadgeVariants({ variant: resolvedVariant }), badgeClassName)}>
        {isTextLike(letter) ? (
          <Text className={quizBadgeTextVariants({ variant: resolvedVariant })}>
            {letter}
          </Text>
        ) : (
          letter
        )}
      </View>
    ) : null;

  return (
    <Pressable
      {...injected}
      ref={ref}
      className={containerClass}
      disabled={disabled}
      haptic={haptic}
      onPress={onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      testID={testID}
      webProps={webProps}
      nativeProps={nativeProps}
      // A light press-in on touch (native only — the web ignores it, and the
      // Figma pressed state there stays a colour step).
      pressScale={disabled ? undefined : QUIZ_BUTTON_PRESS_SCALE}>
      <QuizButtonCrossfade containerClass={containerClass} />
      {badge}
      {label}
    </Pressable>
  );
}

/**
 * Public QuizButton. `forwardRef` so `asChild` consumers can hand the
 * underlying DOM element back for positioning / focus management. Cast
 * through `unknown` to detach from React's `Ref<T>` generic — see `AnyRef`
 * and the analogous pattern in `Pressable.tsx` / `Button.tsx` / `Chip.tsx`.
 */
export const QuizButton = forwardRef(
  QuizButtonInner as never,
) as unknown as React.ForwardRefExoticComponent<
  QuizButtonProps & { ref?: AnyRef<HTMLElement> }
>;
(QuizButton as { displayName?: string }).displayName = 'QuizButton';

/** How far a quiz option sinks under the finger (native). */
const QUIZ_BUTTON_PRESS_SCALE = 0.98;

function isTextLike(c: unknown): boolean {
  if (c == null || typeof c === 'boolean') return false;
  if (typeof c === 'string' || typeof c === 'number') return true;
  if (Array.isArray(c)) return c.every(isTextLike);
  return false;
}
